import { useEffect, useRef, useState } from "react";
import api, { DocumentTranslation } from "../../../../hooks/api";
import { Setting } from "./Settings";
import classNames from "./DocumentTranslation.module.css";
import { useAxios } from "../../../../hooks/axios";
import Loader from "../../../common/Loader";
import Button from "../../../common/Button";
import ConfirmActionDialog from "../../../common/dialogs/ConfirmActionDialog";
import { getLanguageFlag, getLanguageName } from "../../../../types";
import {
  IoClose,
  IoDocumentTextOutline,
  IoCloudDownloadOutline,
} from "react-icons/io5";
import { toast } from "react-toastify";
import axios from "axios";
import LanguageMultiSelect from "../../../common/LanguageMultiSelect";

const s3Axios = import.meta.env.VITE_STUB ? null : axios.create();

function StatusBadge({ status }: { status: DocumentTranslation["status"] }) {
  return (
    <span
      className={`${classNames.StatusBadge} ${classNames[`Status_${status}`]}`}
    >
      {status === "processing" && "Extracting..."}
      {status === "completed" && "Completed"}
      {status === "failed" && "Failed"}
    </span>
  );
}

const PROGRESS_CHECKPOINTS = [30, 60, 85, 100];

function DocumentRow({
  doc,
  onDelete,
  onDownload,
}: {
  doc: DocumentTranslation;
  onDelete: () => void;
  onDownload: () => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [displayProgress, setDisplayProgress] = useState(
    Math.max(doc.progress, 5),
  );

  useEffect(() => {
    setDisplayProgress((prev) => Math.max(prev, doc.progress || 5));
  }, [doc.progress]);

  useEffect(() => {
    if (doc.status !== "processing") return;
    const nextCheckpoint =
      PROGRESS_CHECKPOINTS.find((c) => c > doc.progress) ?? 100;
    const cap = nextCheckpoint - 2;
    const interval = setInterval(() => {
      setDisplayProgress((prev) =>
        prev >= cap ? prev : Math.min(prev + 0.2, cap),
      );
    }, 400);
    return () => clearInterval(interval);
  }, [doc.progress, doc.status]);

  const targetLanguages = doc.target_language
    ? doc.target_language.split(",").filter(Boolean)
    : [];

  return (
    <div className={classNames.DocumentRow}>
      <div className={classNames.DocumentIcon}>
        <IoDocumentTextOutline />
      </div>
      <div className={classNames.DocumentInfo}>
        <div className={classNames.DocumentName}>{doc.original_filename}</div>
        <div className={classNames.DocumentMeta}>
          {targetLanguages.map((lang, i) => (
            <span key={lang}>
              {i > 0 && <span className={classNames.Dot}>·</span>}
              {getLanguageFlag(lang)} {getLanguageName(lang)}
            </span>
          ))}
          {targetLanguages.length > 0 && (
            <span className={classNames.Dot}>·</span>
          )}
          <span>{new Date(doc.created_at).toLocaleDateString()}</span>
        </div>
        {doc.status === "processing" && (
          <div className={classNames.ProgressBarWrapper}>
            <div
              className={classNames.ProgressBar}
              style={{ width: `${displayProgress}%` }}
            />
          </div>
        )}
        {doc.status === "failed" && doc.error_message && (
          <div className={classNames.ErrorMessage}>{doc.error_message}</div>
        )}
        {doc.status === "completed" &&
          doc.key_terms &&
          !Array.isArray(doc.key_terms) && (
            <div className={classNames.KeyTermsSection}>
              {Object.entries(doc.key_terms).map(([lang, terms]) =>
                Array.isArray(terms) ? (
                  <div key={lang} className={classNames.KeyTermsLanguage}>
                    <span className={classNames.KeyTermsLanguageLabel}>
                      {getLanguageFlag(lang)} {getLanguageName(lang)}
                    </span>
                    <div className={classNames.KeyTerms}>
                      {terms.map((term, i) => (
                        <span key={i} className={classNames.KeyTerm}>
                          {term}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null,
              )}
            </div>
          )}
      </div>
      <div className={classNames.DocumentActions}>
        {doc.status !== "processing" && <StatusBadge status={doc.status} />}
        <button
          className={classNames.IconButton}
          title="Download original"
          onClick={onDownload}
        >
          <IoCloudDownloadOutline />
        </button>
        <ConfirmActionDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          trigger={
            <button
              className={classNames.IconButton}
              onClick={() => setConfirmOpen(true)}
            >
              <IoClose />
            </button>
          }
          title="Delete document"
          action="Delete"
          content={
            <p>
              Are you sure you want to delete{" "}
              <strong>{doc.original_filename}</strong>? This will remove the
              uploaded file and all extracted keywords.
            </p>
          }
          onConfirm={() => {
            onDelete();
            setConfirmOpen(false);
          }}
        />
      </div>
    </div>
  );
}

export default function GlossaryBuilder() {
  const axiosInstance = useAxios();
  const getUploadUrl = api.useGetDocumentTranslationUploadUrl(axiosInstance);
  const processDocument = api.useProcessDocumentTranslation(axiosInstance);
  const getList = api.useGetDocumentTranslationList(axiosInstance);
  const getDownloadUrl =
    api.useGetDocumentTranslationDownloadUrl(axiosInstance);
  const deleteDocument = api.useDeleteDocumentTranslation(axiosInstance);

  const [documents, setDocuments] = useState<DocumentTranslation[]>();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getList();
        setDocuments(res.data.documents);
      } finally {
        setLoading(false);
      }
    })();
  }, [getList]);

  useEffect(() => {
    if (!documents) return;
    const hasProcessing = documents.some((d) => d.status === "processing");
    if (!hasProcessing) return;

    const interval = setInterval(async () => {
      try {
        const res = await getList();
        setDocuments(res.data.documents);
      } catch {
        // ignore poll errors
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [documents, getList]);

  const handleFileSelect = async (file: File) => {
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast(
        <div className="Toast ErrorToast">Only PDF files are supported.</div>,
      );
      return;
    }

    setUploading(true);
    let createdDocId: string | undefined;
    try {
      const { data } = await getUploadUrl({
        filename: file.name,
        targetLanguages: selectedLanguages,
      });
      createdDocId = data.document.id;

      await (s3Axios ?? axiosInstance).put(data.uploadUrl, file, {
        headers: { "Content-Type": "application/pdf" },
      });

      const { data: processData } = await processDocument(data.document.id);
      createdDocId = undefined;
      setDocuments((prev) => [processData.document, ...(prev ?? [])]);
    } catch (err) {
      console.error("Upload failed:", err);
      if (createdDocId) {
        deleteDocument(createdDocId).catch(() => {});
      }
      const message = axios.isAxiosError(err)
        ? (err.response?.data?.message ?? "Upload failed. Please try again.")
        : "Upload failed. Please try again.";
      toast(<div className="Toast ErrorToast">{message}</div>);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownload = async (docId: string) => {
    try {
      const { data } = await getDownloadUrl(docId, "original");
      window.open(data.url, "_blank");
    } catch {
      alert("Download link unavailable.");
    }
  };

  const handleDelete = async (docId: string) => {
    try {
      await deleteDocument(docId);
      setDocuments((prev) => prev?.filter((d) => d.id !== docId));
    } catch {
      alert("Failed to delete document.");
    }
  };

  if (loading) return <Loader />;

  return (
    <Setting
      title="Fine-tuning builder"
      subtitle="Improve translation accuracy by uploading a relevant PDF (slide deck, brief, or report) before your meeting"
    >
      <div className={classNames.DocumentTranslation}>
        <div className={classNames.UploadSection}>
          <div className={classNames.UploadControls}>
            <LanguageMultiSelect
              selectedLanguages={selectedLanguages}
              onChange={setSelectedLanguages}
              maxLanguages={5}
            />
            <Button
              backgroundColor="primary"
              disabled={uploading || selectedLanguages.length < 2}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? "Uploading..." : "Upload a PDF"}
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            className={classNames.HiddenInput}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
          />
          <p className={classNames.UploadHint}>
            Select at least 2 languages, then upload a PDF under 40 pages.
          </p>
        </div>

        {documents && documents.length > 0 ? (
          <div className={classNames.DocumentList}>
            {documents.map((doc) => (
              <DocumentRow
                key={doc.id}
                doc={doc}
                onDelete={() => handleDelete(doc.id)}
                onDownload={() => handleDownload(doc.id)}
              />
            ))}
          </div>
        ) : (
          <div className={classNames.EmptyState}>
            <IoDocumentTextOutline />
            <p>No documents uploaded yet.</p>
          </div>
        )}
      </div>
    </Setting>
  );
}
